// @ts-nocheck -- legacy typings pending cleanup; runtime behavior unchanged
import { z } from "zod";

export type VitalAlertLevel = "info" | "attention" | "urgent";

export type VitalRedFlags = {
  chest_pain?: boolean;
  dyspnea?: boolean;
  syncope?: boolean;
  confusion?: boolean;
  seizure?: boolean;
  unresponsive?: boolean;
  new_neurologic_deficit?: boolean;
  vision_change?: boolean;
};

export type VitalContext = {
  posture?: "repouso" | "sentado" | "em_pe" | "atividade" | "pos_atividade" | "desconhecido";
  measurement_source?: "manual" | "relogio" | "oximetro" | "esfigmomanometro" | "termometro" | "outro" | "desconhecido";
  sleep_hours_last_24h?: number;
  recent_exertion?: boolean;
  hot_environment?: boolean;
  caffeine_near_measurement?: boolean;
  symptoms_text?: string;
};

export type NormalizedVitalRecord = {
  id?: string;
  patient_id?: string;
  measured_at: string;
  systolic_mm_hg?: number | null;
  diastolic_mm_hg?: number | null;
  heart_rate_bpm?: number | null;
  spo2_percent?: number | null;
  respiratory_rate_bpm?: number | null;
  temperature_celsius?: number | null;
  glucose_mg_dl?: number | null;
  weight_kg?: number | null;
  posture?: string | null;
  measurement_source?: string | null;
  context?: string | null;
  context_structured?: Record<string, unknown> | null;
  red_flags?: VitalRedFlags | null;
  clinical_note?: string | null;
  linked_dose_id?: string | null;
  created_at?: string | null;
  __source_table?: "vital_signs" | "patient_vital_signs";
};

export type VitalAlert = {
  id: string;
  level: VitalAlertLevel;
  title: string;
  detail: string;
  sourceLabel?: string;
  sourceUrl?: string;
};

const optionalFinite = (min: number, max: number) =>
  z.number().finite().min(min).max(max).nullable().optional();

export const vitalRecordSchema = z.object({
  measured_at: z.string().refine((value) => Number.isFinite(new Date(value).getTime()), "Data/hora inválida"),
  systolic_mm_hg: optionalFinite(40, 300),
  diastolic_mm_hg: optionalFinite(20, 200),
  heart_rate_bpm: optionalFinite(20, 260),
  spo2_percent: optionalFinite(50, 100),
  respiratory_rate_bpm: optionalFinite(2, 80),
  temperature_celsius: optionalFinite(30, 45),
  glucose_mg_dl: optionalFinite(20, 1000),
  weight_kg: optionalFinite(1, 500),
  posture: z.string().max(40).nullable().optional(),
  measurement_source: z.string().max(60).nullable().optional(),
  context: z.string().max(2000).nullable().optional(),
  clinical_note: z.string().max(4000).nullable().optional(),
  linked_dose_id: z.string().uuid().nullable().optional(),
  context_structured: z.record(z.unknown()).nullable().optional(),
  red_flags: z
    .object({
      chest_pain: z.boolean().optional(),
      dyspnea: z.boolean().optional(),
      syncope: z.boolean().optional(),
      confusion: z.boolean().optional(),
      seizure: z.boolean().optional(),
      unresponsive: z.boolean().optional(),
      new_neurologic_deficit: z.boolean().optional(),
      vision_change: z.boolean().optional(),
    })
    .nullable()
    .optional(),
}).refine(
  (row) => [
    row.systolic_mm_hg,
    row.diastolic_mm_hg,
    row.heart_rate_bpm,
    row.spo2_percent,
    row.respiratory_rate_bpm,
    row.temperature_celsius,
    row.glucose_mg_dl,
    row.weight_kg,
  ].some((value) => value != null),
  "Informe ao menos uma medida, como FC ou PA.",
);

export function numberOrNull(value: unknown): number | null {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeVitalRecord(row: any): NormalizedVitalRecord {
  return {
    id: row?.id,
    patient_id: row?.patient_id,
    measured_at: row?.measured_at ?? row?.created_at ?? new Date().toISOString(),
    systolic_mm_hg: numberOrNull(row?.systolic_mm_hg ?? row?.systolic_bp),
    diastolic_mm_hg: numberOrNull(row?.diastolic_mm_hg ?? row?.diastolic_bp),
    heart_rate_bpm: numberOrNull(row?.heart_rate_bpm ?? row?.heart_rate),
    spo2_percent: numberOrNull(row?.spo2_percent ?? row?.oxygen_saturation),
    respiratory_rate_bpm: numberOrNull(row?.respiratory_rate_bpm),
    temperature_celsius: numberOrNull(row?.temperature_celsius),
    glucose_mg_dl: numberOrNull(row?.glucose_mg_dl),
    weight_kg: numberOrNull(row?.weight_kg),
    posture: row?.posture ?? row?.context_structured?.posture ?? null,
    measurement_source: row?.measurement_source ?? row?.context_structured?.measurement_source ?? null,
    context: row?.context ?? null,
    context_structured: row?.context_structured ?? null,
    red_flags: row?.red_flags ?? null,
    clinical_note: row?.clinical_note ?? row?.notes ?? null,
    linked_dose_id: row?.linked_dose_id ?? null,
    created_at: row?.created_at ?? null,
    __source_table: row?.__source_table ?? (row?.heart_rate_bpm !== undefined || row?.systolic_mm_hg !== undefined ? "vital_signs" : "patient_vital_signs"),
  };
}

const urgentFlagLabels: Array<[keyof VitalRedFlags, string]> = [
  ["chest_pain", "dor no peito"],
  ["dyspnea", "dispneia importante"],
  ["syncope", "síncope/desmaio"],
  ["confusion", "confusão aguda"],
  ["seizure", "convulsão"],
  ["unresponsive", "não responsividade/dificuldade para despertar"],
  ["new_neurologic_deficit", "déficit neurológico novo"],
  ["vision_change", "alteração visual aguda"],
];

export function assessVitalRecord(input: NormalizedVitalRecord): VitalAlert[] {
  const row = normalizeVitalRecord(input);
  const alerts: VitalAlert[] = [];
  const redFlags = row.red_flags ?? {};
  const activeFlags = urgentFlagLabels.filter(([key]) => redFlags[key]).map(([, label]) => label);

  if (activeFlags.length) {
    alerts.push({
      id: "clinical-red-flags",
      level: "urgent",
      title: "Sinais clínicos de alerta",
      detail: `Registrado: ${activeFlags.join(", ")}. A avaliação clínica prevalece sobre qualquer valor isolado do monitor.`,
    });
  }

  if (row.spo2_percent != null) {
    if (row.spo2_percent < 90) {
      alerts.push({
        id: "spo2-emergency",
        level: "urgent",
        title: "SpO₂ < 90%",
        detail:
          "Hipoxemia importante. Reconfira rapidamente posicionamento/perfusão do sensor, sem atrasar avaliação urgente se o quadro clínico for compatível.",
        sourceLabel: "WHO Pulse Oximetry Training Manual",
        sourceUrl:
          "https://cdn.who.int/media/docs/default-source/patient-safety/pulse-oximetry/who-ps-pulse-oxymetry-training-manual-en.pdf",
      });
    } else if (row.spo2_percent <= 94) {
      alerts.push({
        id: "spo2-low",
        level: "attention",
        title: "SpO₂ ≤ 94%",
        detail:
          "Valor abaixo da faixa usual em pessoas saudáveis. Repetir a medida em condições técnicas adequadas e interpretar junto aos sintomas.",
        sourceLabel: "WHO Pulse Oximetry Training Manual",
        sourceUrl:
          "https://cdn.who.int/media/docs/default-source/patient-safety/pulse-oximetry/who-ps-pulse-oxymetry-training-manual-en.pdf",
      });
    }
  }

  const severeBp =
    (row.systolic_mm_hg != null && row.systolic_mm_hg > 180) ||
    (row.diastolic_mm_hg != null && row.diastolic_mm_hg > 120);
  if (severeBp) {
    alerts.push({
      id: "severe-bp",
      level: activeFlags.length ? "urgent" : "attention",
      title: activeFlags.length ? "PA muito elevada + sintomas de alerta" : "PA em faixa de hipertensão grave",
      detail: activeFlags.length
        ? "PA >180 e/ou >120 mmHg com sintomas de alarme requer avaliação emergencial."
        : "Repetir a medida após pelo menos 1 minuto em condições adequadas. Se persistir, requer contato clínico rápido; número isolado não define emergência hipertensiva.",
      sourceLabel: "American Heart Association",
      sourceUrl:
        "https://www.heart.org/en/health-topics/high-blood-pressure/understanding-blood-pressure-readings/when-to-call-911-for-high-blood-pressure",
    });
  }

  const posture = String(row.posture ?? row.context_structured?.posture ?? "").toLowerCase();
  const resting = /repous|sentad|deitad/.test(posture);
  if (resting && row.heart_rate_bpm != null && (row.heart_rate_bpm < 60 || row.heart_rate_bpm > 100)) {
    alerts.push({
      id: "resting-heart-rate",
      level: "attention",
      title: "FC fora da faixa usual de repouso",
      detail:
        "Em adultos, 60–100 bpm é uma referência usual de repouso. Ansiedade, esforço, condicionamento, febre, hidratação e fármacos/substâncias podem deslocar a FC; o número isolado não classifica gravidade.",
      sourceLabel: "American Heart Association",
      sourceUrl:
        "https://www.heart.org/en/healthy-living/exercise-and-physical-activity/fitness-basics/target-heart-rates",
    });
  }

  return alerts;
}

export function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2) return sorted[middle] ?? null;
  return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
}

export function vitalSummary(rows: any[]) {
  const normalized = rows.map(normalizeVitalRecord).filter((row) => Number.isFinite(new Date(row.measured_at).getTime()));
  const heartRates = normalized.map((row) => row.heart_rate_bpm).filter((value): value is number => value != null);
  const restingHeartRates = normalized
    .filter((row) => /repous|sentad|deitad/.test(String(row.posture ?? "").toLowerCase()))
    .map((row) => row.heart_rate_bpm)
    .filter((value): value is number => value != null);
  const spo2 = normalized.map((row) => row.spo2_percent).filter((value): value is number => value != null);
  const latest = [...normalized].sort(
    (a, b) => new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime(),
  )[0] ?? null;
  return {
    latest,
    count: normalized.length,
    restingHeartRateMedian: median(restingHeartRates),
    heartRateMin: heartRates.length ? Math.min(...heartRates) : null,
    heartRateMax: heartRates.length ? Math.max(...heartRates) : null,
    spo2Min: spo2.length ? Math.min(...spo2) : null,
  };
}

export function vitalContextText(context: VitalContext, redFlags: VitalRedFlags): string {
  const bits = [
    context.posture ? `posição: ${context.posture.replaceAll("_", " ")}` : null,
    context.measurement_source ? `fonte: ${context.measurement_source.replaceAll("_", " ")}` : null,
    context.sleep_hours_last_24h != null ? `sono 24h: ${context.sleep_hours_last_24h} h` : null,
    context.recent_exertion ? "esforço recente" : null,
    context.hot_environment ? "ambiente quente" : null,
    context.caffeine_near_measurement ? "cafeína próxima" : null,
    context.symptoms_text?.trim() || null,
    ...urgentFlagLabels.filter(([key]) => redFlags[key]).map(([, label]) => label),
  ].filter(Boolean);
  return bits.join(" · ");
}
